import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import { ivyAffiliateApis } from "@services/apis/IvyAffiliate";
import { IvyAffiliateMerchant } from "@services/models/IvyAffiliate/GetIvyAffiliateMerchantsModel";
import { IvyAffiliate } from "@services/models/IvyAffiliate/GetIvyAffiliatesModel";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AssignIvyIntroducerSlider from "./components/AssignIvyIntroducerSlider";

const formatPct = (value: string | null | undefined) =>
  value === null || value === undefined ? "—" : value;

const IvyAffiliateManagement = () => {
  const affiliatesGridRef = useRef<any>(null);
  const merchantsGridRef = useRef<any>(null);

  const [affiliates, setAffiliates] = useState<IvyAffiliate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assignSliderOpen, setAssignSliderOpen] = useState(false);

  const [selectedAffiliate, setSelectedAffiliate] =
    useState<IvyAffiliate | null>(null);
  const [merchants, setMerchants] = useState<IvyAffiliateMerchant[]>([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [merchantsError, setMerchantsError] = useState("");

  const affiliatesColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "User ID",
        field: "userId",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: ({ data }: { data: IvyAffiliate }) => (
          <div className="flex items-center justify-between space-x-[6px]">
            <span>{data.userId}</span>
            <CopyButton
              copyText={data.userId.toString()}
              copyMessage="User ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },
      {
        headerName: "Email",
        field: "email",
        flex: 1.6,
        minWidth: 180,
        cellRenderer: ({ data }: { data: IvyAffiliate }) => (
          <div className="flex items-center justify-between space-x-[6px]">
            <span className="truncate">{data.email || "—"}</span>
            {data.email && (
              <CopyButton
                copyText={data.email}
                copyMessage="Email copied!"
                copyIconSize={10}
                buttonSize="14px"
                borderRadius="2px"
              />
            )}
          </div>
        ),
      },
      {
        headerName: "Merchants",
        field: "merchantsCount",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 140,
      },
    ],
    [],
  );

  const merchantsColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Merchant ID",
        field: "merchantId",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: ({ data }: { data: IvyAffiliateMerchant }) => (
          <div className="flex items-center justify-between space-x-[6px]">
            <span>{data.merchantId}</span>
            <CopyButton
              copyText={data.merchantId.toString()}
              copyMessage="Merchant ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },
      {
        headerName: "Merchant Name",
        field: "merchantName",
        flex: 1.4,
        minWidth: 150,
        valueFormatter: (params) => params.value || "—",
      },
      {
        headerName: "Pay-in %",
        field: "payinRefPct",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => formatPct(params.value),
      },
      {
        headerName: "Pay-out %",
        field: "payoutRefPct",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => formatPct(params.value),
      },
    ],
    [],
  );

  const fetchAffiliates = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await ivyAffiliateApis.getIvyAffiliates();
      setAffiliates(response.data || []);
    } catch (err: any) {
      setAffiliates([]);
      setError(err?.response?.data?.msg || "Failed to retrieve Ivy affiliates");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMerchants = useCallback(async (userId: number) => {
    try {
      setMerchantsLoading(true);
      setMerchantsError("");
      const response = await ivyAffiliateApis.getIvyAffiliateMerchants(userId);
      setMerchants(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setMerchants([]);
      setMerchantsError(
        err?.response?.data?.msg ||
          "Failed to retrieve Ivy affiliate merchants",
      );
    } finally {
      setMerchantsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAffiliates();
  }, [fetchAffiliates]);

  useEffect(() => {
    if (selectedAffiliate) {
      fetchMerchants(selectedAffiliate.userId);
    } else {
      setMerchants([]);
      setMerchantsError("");
    }
  }, [selectedAffiliate, fetchMerchants]);

  const onAffiliateRowClick = (params: any) => {
    const gridEl = (params.event?.target as HTMLElement | undefined)?.closest(
      ".ag-root-wrapper",
    );

    if (gridEl) {
      gridEl.querySelectorAll(".ag-row").forEach((row) => {
        row.classList.remove("ag-row-selected-custom");
      });

      if (params.rowIndex !== null && params.rowIndex !== undefined) {
        const rowElement = gridEl.querySelector(
          `[row-index="${params.rowIndex}"]`,
        );
        rowElement?.classList.add("ag-row-selected-custom");
      }
    }

    setSelectedAffiliate(params.data as IvyAffiliate);
  };

  const handleAssignSuccess = () => {
    fetchAffiliates();
    if (selectedAffiliate) {
      fetchMerchants(selectedAffiliate.userId);
    }
  };

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex items-center justify-between sticky top-0 z-10 px-[20px] gap-4">
        <p className="text-text-base text-[14px] font-semibold">
          Ivy Affiliate
        </p>
        <LightCustomButton
          text="ASSIGN"
          onClick={() => setAssignSliderOpen(true)}
          isActive={true}
          className="min-w-[90px] shrink-0"
        />
      </div>

      <div className="flex flex-row gap-3 items-start px-4">
        <div
          className="flex flex-col bg-bg-page-body py-[20px] overflow-hidden gap-4 w-[55%]"
          style={{ height: "calc(100dvh - 90px)" }}
        >
          {loading && affiliates.length === 0 ? (
            <AgGridSkeleton
              columnDefs={affiliatesColumnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              ref={affiliatesGridRef}
              columnDefs={affiliatesColumnDefs}
              rowData={affiliates}
              onRowClicked={onAffiliateRowClick}
              getRowId={(params) => params.data.userId.toString()}
              error={error}
            />
          )}
        </div>

        <div
          className="flex flex-col py-[16px] px-[16px] bg-bg-base rounded-lg mt-[20px] w-[45%]"
          style={{ height: "calc(100dvh - 110px)" }}
        >
          {selectedAffiliate ? (
            <div className="flex flex-col gap-2 h-full">
              <div className="flex flex-row items-center justify-between mb-2">
                <div>
                  <p className="text-text-base text-[14px] font-medium">
                    Mapped Merchants
                  </p>
                  <p className="text-text-subtle text-[11px]">
                    {selectedAffiliate.email}
                  </p>
                </div>
                <p className="text-text-base text-[14px] font-medium">
                  {merchants.length}
                </p>
              </div>
              {merchantsLoading ? (
                <AgGridSkeleton
                  columnDefs={merchantsColumnDefs}
                  style={{ height: "100%", width: "100%" }}
                />
              ) : (
                <AgGridReactTable
                  ref={merchantsGridRef}
                  columnDefs={merchantsColumnDefs}
                  rowData={merchants}
                  getRowId={(params) => params.data.merchantId.toString()}
                  error={merchantsError}
                />
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-text-base text-[12px]">
              Select an affiliate to view merchants
            </div>
          )}
        </div>
      </div>

      <AssignIvyIntroducerSlider
        isOpen={assignSliderOpen}
        onClose={() => setAssignSliderOpen(false)}
        onSuccess={handleAssignSuccess}
      />
    </>
  );
};

export default IvyAffiliateManagement;
